import React, { useState } from "react";
import CardBase from "../../cards/CardBase";
import { Button } from "../../ui/button";
import { Input } from "../../ui/InputDefault";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { createType } from "@/lib/stores/typesStore";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { UnsavedChangesDialog } from "@/components/ui/UnsavedChangesDialog";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { Loader2 } from 'lucide-react';

interface VariableWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (variableId: string) => void;
  folderId?: string | null;
}

const ELEMENT_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'range', label: 'Range' },
  { value: 'type', label: 'Type' },
  { value: 'boolean', label: 'Boolean' },
];

export default function VariableWizard({
  isOpen,
  onClose,
  onSuccess,
  folderId,
}: VariableWizardProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  
  const [variableName, setVariableName] = useState('');
  const [elementType, setElementType] = useState('string');
  const [description, setDescription] = useState('');
  const [groupName, setGroupName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges(
    {},
    () => ({
      variableName,
      elementType,
      description,
      groupName,
    }),
    true
  );

  const handleSubmit = async () => {
    if (!selectedGame || !variableName.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in the variable name',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const newVariable = await createType(
        {
          game_id: selectedGame.id,
          name: variableName.trim(),
          element_type: elementType,
          group: groupName.trim() || undefined,
          data: [], // Empty data initially, elements will be added later
          description: description.trim() || undefined,
          folder: folderId || undefined,
        },
        toast
      );

      toast({
        title: 'Success',
        description: 'Variable created successfully',
      });

      // Mark as saved
      unsavedChanges.markAsSaved();

      // Reset form
      setVariableName('');
      setElementType('string');
      setDescription('');
      setGroupName('');

      if (onSuccess) {
        onSuccess(newVariable.id);
      }

      onClose();
    } catch (error) {
      console.error('Error creating variable:', error);
      // Error toast is handled by createType
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Check for unsaved changes
    const hasUnsavedChanges = unsavedChanges.checkForChanges();

    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      return;
    }

    // No unsaved changes, proceed with close
    proceedWithClose();
  };

  const proceedWithClose = () => {
    setVariableName('');
    setElementType('string');
    setDescription('');
    setGroupName('');
    unsavedChanges.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex justify-center items-center">
        {/* Blurred white overlay */}
        <div
          className="absolute inset-0 bg-white/80 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal content using CardBase */}
        <div className="relative z-10 w-96 max-w-md">
          <CardBase className="flex flex-col w-full" shadowType="none">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Create New Variable
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Create a new variable with basic information
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Variable Name */}
              <div>
                <Label htmlFor="variable-name" className="text-sm font-medium text-gray-700">
                  Variable Name *
                </Label>
                <Input
                  id="variable-name"
                  value={variableName}
                  onChange={(e) => setVariableName(e.target.value)}
                  placeholder="Enter variable name"
                  className="mt-1"
                  autoFocus
                />
              </div>

              {/* Element Type */}
              <div>
                <Label htmlFor="element-type" className="text-sm font-medium text-gray-700">
                  Value Type *
                </Label>
                <select
                  id="element-type"
                  value={elementType}
                  onChange={(e) => setElementType(e.target.value)}
                  className="mt-1 w-full h-10 px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {ELEMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {elementType === 'string' && 'Text values'}
                  {elementType === 'number' && 'Numeric values'}
                  {elementType === 'percentage' && 'Percentage values (0-100)'}
                  {elementType === 'range' && 'Range values with min/max/increment'}
                  {elementType === 'type' && 'Enumerated type values'}
                  {elementType === 'boolean' && 'Boolean values (true/false)'}
                </p>
              </div>

              {/* Group Name */}
              <div>
                <Label htmlFor="group-name" className="text-sm font-medium text-gray-700">
                  Group (Optional)
                </Label>
                <Input
                  id="group-name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  className="mt-1"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                  Description (Optional)
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter variable description"
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                onClick={handleSubmit}
                disabled={isSubmitting || !variableName.trim()}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isSubmitting ? 'Creating...' : 'Create Variable'}
              </Button>
            </div>
          </CardBase>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
        }}
        onSave={async () => {
          await handleSubmit();
          setShowUnsavedDialog(false);
        }}
        onDiscard={() => {
          unsavedChanges.reset();
          proceedWithClose();
          setShowUnsavedDialog(false);
        }}
        title="Unsaved Variable Changes"
        description="You have unsaved changes to this variable. Would you like to save them before closing?"
      />
    </>
  );
}
